import type {
  PatientAccessMode,
  TenantRoleKey,
} from "@amplecare/identity-model";
import type { PatientPortalFeatureFlagsV1 } from "./patient-workspace-contract.js";
import type { WorkspaceChangedScopeV1 } from "./workspace-refresh-contract.js";

export const tenantAdminSummaryContractVersion =
  "tenant-admin-summary-v1" as const;

export const tenantAdminSummaryRoutePath =
  "/api/v1/tenant-admin/summary" as const;

export const tenantAdminCommandContractVersion =
  "tenant-admin-command-v1" as const;

export const tenantAdminCommandRoutePath =
  "/api/v1/tenant-admin/commands" as const;

export const tenantAdminPatientOperationsContractVersion =
  "tenant-admin-patient-operations-v1" as const;

export const tenantAdminPatientOperationsRoutePath =
  "/api/v1/tenant-admin/patient-operations" as const;

export const tenantAdminClinicianOperationsContractVersion =
  "tenant-admin-clinician-operations-v1" as const;

export const tenantAdminClinicianOperationsRoutePath =
  "/api/v1/tenant-admin/clinician-operations" as const;

export const tenantAdminReviewQueueItemsContractVersion =
  "tenant-admin-review-queue-items-v1" as const;

export const tenantAdminReviewQueueItemsRoutePath =
  "/api/v1/tenant-admin/review-queue-items" as const;

export interface TenantAdminSummaryRequestV1 {
  tenantId: string;
  analyticsStartAt?: string | null;
  analyticsEndAt?: string | null;
  analyticsDepartmentCode?: string | null;
}

export type TenantAdminPatientOperationsSortKey =
  | "name"
  | "program"
  | "status"
  | "updated"
  | "attention"
  | "documents"
  | "messages";

export type TenantAdminPatientOperationsSortDirection = "asc" | "desc";

export type TenantAdminPatientOperationsCareTypeFilter =
  | "all"
  | "ololsh-source"
  | "chronic"
  | "procedure"
  | "pain-management";

export type TenantAdminPatientOperationsLifecycleFilter =
  | "current"
  | "archived"
  | "all";

export type TenantAdminClinicianOperationsSortKey =
  | "name"
  | "department"
  | "role"
  | "status"
  | "updated";

export type TenantAdminClinicianOperationsSortDirection = "asc" | "desc";

export type TenantAdminReviewQueueId =
  | "pending-reviews"
  | "review-required-tasks"
  | "active-assignments"
  | "open-conversations"
  | "active-escalations"
  | "available-documents"
  | "unmapped-audits"
  | "fallback-audits";

export type TenantAdminReviewQueueActionKind =
  | "open-backlog"
  | "route-owner"
  | "request-audit"
  | "escalate";

export type TenantAdminReviewQueueOwnerKind =
  | "clinician"
  | "team"
  | "department"
  | "program"
  | "queue"
  | "manual_handoff";

export type TenantAdminReviewQueueItemKind =
  | "message"
  | "task"
  | "assignment"
  | "conversation"
  | "escalation"
  | "document"
  | "mapping-audit";

export type TenantAdminPatientPortalRoute =
  | "overview"
  | "onboarding"
  | "appointments"
  | "procedures"
  | "medications"
  | "documents"
  | "messages";

export type TenantAdminPatientPortalControlActionKind =
  | "complete-onboarding"
  | "reset-onboarding"
  | "reset-auth"
  | "set-reminder-preference"
  | "set-entry-route";

export type TenantAdminPatientCommandStatus =
  | "draft"
  | "active"
  | "inactive";

export type TenantAdminPatientLifecycleStatus =
  | TenantAdminPatientCommandStatus
  | "archived";

export type TenantAdminPatientAccessSyncStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export interface TenantAdminSummaryConnectorStatusCountV1 {
  status: string;
  count: number;
}

export interface TenantAdminSummaryConnectorRegistrationV1 {
  connectorId: string;
  displayName: string;
  connectorFamily: string;
  sourceSystemKey: string;
  sourceAuthorityMode: string;
  status: string;
  siteCode: string | null;
  departmentCode: string | null;
  teamKey: string | null;
  scopeLabel: string;
  lastSyncAt: string | null;
  lastSyncRequestedAt: string | null;
  lastSyncCommandId: string | null;
  syncInvocationStatus: string | null;
  syncInvocationRequestId: string | null;
  syncInvocationFunctionName: string | null;
  syncStatus: string | null;
  authReviewStatus: string | null;
  authReviewNotes: string | null;
  authReviewedAt: string | null;
  metadataSummary: string | null;
  updatedAt: string;
}

export interface TenantAdminSummaryProvisioningSnapshotV1 {
  recentRunCount: number;
  activeAllocationCount: number;
  activeClusterCount: number;
  latestRun:
    | {
        runKind: string;
        runStatus: string;
        startedAt: string | null;
        completedAt: string | null;
      }
    | null;
  recentRuns: TenantAdminSummaryProvisioningRunV1[];
  allocations: TenantAdminSummaryDatabaseAllocationV1[];
  clusters: TenantAdminSummaryDatabaseClusterV1[];
}

export interface TenantAdminSummaryProvisioningRunV1 {
  runId: string;
  runKind: string;
  runStatus: string;
  environmentKey: string;
  targetLabel: string;
  databaseName: string | null;
  clusterIdentifier: string | null;
  requestedBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface TenantAdminSummaryDatabaseAllocationV1 {
  allocationId: string;
  databaseName: string;
  allocationStatus: string;
  isolationModel: string;
  migrationBundleKey: string;
  lastMigrationVersion: string | null;
  lastProvisionedAt: string | null;
  clusterIdentifier: string;
  clusterStatus: string;
  awsRegion: string;
  updatedAt: string;
}

export interface TenantAdminSummaryDatabaseClusterV1 {
  clusterId: string;
  clusterIdentifier: string;
  environmentKey: string;
  awsRegion: string;
  clusterRole: string;
  engineFamily: string;
  engineVersion: string;
  clusterStatus: string;
  allocationCount: number;
  updatedAt: string;
}

export interface TenantAdminSummaryWorkflowSnapshotV1 {
  openConversationCount: number;
  pendingMessageReviewCount: number;
  activeTaskAssignmentCount: number;
  reviewRequiredTaskCount: number;
  activeJourneyCount: number;
  activeEscalationCount: number;
  availableDocumentCount: number;
}

export interface TenantAdminSummaryAuditSnapshotV1 {
  unmappedResolutionCount: number;
  fallbackResolutionCount: number;
  derivedStateEvaluationCount: number;
}

export interface TenantAdminSummaryActivitySnapshotV1 {
  latestMessageAt: string | null;
  latestDocumentAt: string | null;
}

export interface TenantAdminSummaryAnalyticsSeriesPointV1 {
  label: string;
  autonomousResponsePercent: number;
  clinicianApprovedPercent: number;
  manualEntryPercent: number;
}

export interface TenantAdminSummaryAnalyticsSnapshotV1 {
  activePatientCount: number;
  activePatientDeltaPercent: number | null;
  aiResponseCount: number;
  aiResponseDeltaPercent: number | null;
  averageResponseTimeSeconds: number | null;
  averageResponseTimeDeltaSeconds: number | null;
  patientMessageCount: number;
  patientMessageDeltaPercent: number | null;
  messagesPerPatient: number;
  messagesPerPatientDeltaPercent: number | null;
  aiConfidencePercent: number | null;
  aiConfidenceDeltaPercent: number | null;
  receivedInvitationCount: number;
  loggedInToAppCount: number;
  engagedAgentCount: number;
  loggedInDeltaCount: number;
  engagedDeltaCount: number;
  loggedInConversionPercent: number;
  engagedConversionPercent: number;
  voiceCallCount: number;
  voiceCallDeltaPercent: number | null;
  voiceMinutes: number;
  voiceMinutesDeltaPercent: number | null;
  voiceResponseCount: number;
  voiceResponseDeltaPercent: number | null;
  estimatedClinicianMinutesSaved: number;
  estimatedClinicianMinutesSavedDeltaPercent: number | null;
  languageModel: {
    aiRating: number | null;
    aiRatingDelta: number | null;
    autonomousResponsePercent: number;
    autonomousResponseDeltaPercent: number;
    clinicianApprovedPercent: number;
    clinicianApprovedDeltaPercent: number;
    manualEntryPercent: number;
    manualEntryDeltaPercent: number;
  };
  accuracySeries: TenantAdminSummaryAnalyticsSeriesPointV1[];
}

export interface TenantAdminSummaryWorkforceSubjectV1 {
  subjectKey: string;
  displayName: string | null;
  siteId: string | null;
  clinicianUserId: string;
  patientId: string | null;
  roleKeys: TenantRoleKey[];
  patientAccessMode: PatientAccessMode;
}

export interface TenantAdminSummaryPatientOperationV1 {
  patientId: string;
  displayName: string;
  givenName: string | null;
  familyName: string | null;
  identifier: string | null;
  dateOfBirth: string | null;
  sex: "female" | "male" | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  secondaryPhoneNumber: string | null;
  workPhoneNumber: string | null;
  homePhoneNumber: string | null;
  emergencyContactPhone: string | null;
  alternateEmergencyContactPhone: string | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  contactWindowText: string | null;
  statusLabel: string;
  lifecycleVersion: number;
  archivedAt: string | null;
  archivedByReference: string | null;
  archiveReasonKey: string | null;
  archiveNotes: string | null;
  preArchiveStatus: TenantAdminPatientCommandStatus | null;
  accessSyncStatus: TenantAdminPatientAccessSyncStatus | null;
  accessSyncLastErrorMessage: string | null;
  siteId: string | null;
  leadingClinicianUserId: string | null;
  leadingClinicianLabel: string | null;
  programLabel: string | null;
  nextAppointmentText: string | null;
  pendingDocumentCount: number;
  unreadMessageCount: number;
  requiresAttention: boolean;
  summaryRefreshStatus: string | null;
  summaryGeneratedAt: string | null;
  summaryPolicyVersion: string | null;
  lastUpdatedAt: string | null;
  onboardingInitialFlowComplete: boolean;
  portalAuthStatus: string;
  portalEntryPointRoute: TenantAdminPatientPortalRoute;
  remindersEnabled: boolean;
  reminderWakeUpTime: string | null;
  portalStateUpdatedAt: string | null;
}

export interface TenantAdminClinicianOperationV1 {
  clinicianUserId: string;
  displayName: string;
  emailAddress: string | null;
  phoneNumber: string | null;
  assignedPatientCount: number;
  principalStatus: string;
  membershipStatus: string;
  primaryRoleKey: TenantRoleKey;
  roleKeys: TenantRoleKey[];
  isProtectedOwner: boolean;
  protectedOwnerLabel: string | null;
  patientAccessMode: PatientAccessMode;
  siteCode: string | null;
  siteDisplayName: string | null;
  departmentCode: string | null;
  departmentDisplayName: string | null;
  teamKey: string | null;
  teamDisplayName: string | null;
  membershipId: string | null;
  lastUpdatedAt: string | null;
}

export interface TenantAdminSummaryDepartmentV1 {
  departmentId: string;
  departmentCode: string;
  displayName: string;
  status: string;
  siteCode: string | null;
  siteLabel: string;
  clinicianCount: number;
  updatedAt: string;
}

export interface TenantAdminSummaryTeamV1 {
  teamId: string;
  teamKey: string;
  displayName: string;
  status: string;
  departmentCode: string | null;
  clinicianCount: number;
  updatedAt: string;
}

export interface TenantAdminSummaryFeatureFlagV1 {
  flagId: string;
  flagKey: string;
  flagVariant: string | null;
  enabled: boolean;
  state: "enabled" | "disabled" | "unset";
  isSystemFlag: boolean;
  scope: "tenant" | "site" | "department" | "program";
  scopeKey: string | null;
  scopeLabel: string;
  rolloutNote: string | null;
  updatedAt: string;
}

/**
 * Tenant configuration for the Phase O-6 auto-callback scheduler, edited from
 * the admin console's outbound section. Mirrors
 * tenant_app.outbound_callback_scheduler_settings; a tenant without a saved
 * row runs on the scheduler's code defaults.
 */
export interface TenantAdminCallbackSchedulerSettingsV1 {
  /** Tenant switch; the deployment-level scheduler flag still applies. */
  callbacksEnabled: boolean;
  /** Quiet minutes before the first callback dial and between dials. */
  retryBreakMinutes: number;
  /** Dials one callback request may receive before the scheduler gives up. */
  maxAttemptsPerRequest: number;
  updatedAt: string | null;
  /** False when this environment's tenant database predates the table. */
  configurable: boolean;
}

export type TenantAdminAiModelKeyV1 = "chat" | "voice";
export type TenantAdminAiModelArtifactModelKeyV1 =
  | TenantAdminAiModelKeyV1
  | "shared";
export type TenantAdminAiModelArtifactStatusV1 = "active" | "archived";

export type TenantAdminAiModelContextFlagKeyV1 =
  | "patientDocumentation"
  | "doctorNotes"
  | "schedulingInformation"
  | "billingInformation"
  | "escalationToClinician"
  | "recentCallHistory";

export interface TenantAdminAiModelContextFlagV1 {
  flagKey: TenantAdminAiModelContextFlagKeyV1;
  labelText: string;
  descriptionText: string;
  chatEnabled: boolean;
  chatEditable: boolean;
  voiceEnabled: boolean;
  voiceEditable: boolean;
}

export interface TenantAdminAiModelPromptArtifactV1 {
  promptId: string;
  modelKey: TenantAdminAiModelArtifactModelKeyV1;
  labelText: string;
  purposeText: string;
  promptText: string;
  status: TenantAdminAiModelArtifactStatusV1;
  updatedAt: string | null;
  updatedByCommandId: string | null;
  updatedByTenantAdmin: string | null;
}

export interface TenantAdminAiModelSourceDocumentV1 {
  sourceDocumentId: string;
  modelKey: TenantAdminAiModelArtifactModelKeyV1;
  titleText: string;
  summaryText: string;
  sourceText: string;
  status: TenantAdminAiModelArtifactStatusV1;
  updatedAt: string | null;
  updatedByCommandId: string | null;
  updatedByTenantAdmin: string | null;
}

export interface TenantAdminAiModelParametersV1 {
  contextFlags: TenantAdminAiModelContextFlagV1[];
  promptArtifacts: TenantAdminAiModelPromptArtifactV1[];
  sourceDocuments: TenantAdminAiModelSourceDocumentV1[];
  updatedAt: string | null;
  updatedByCommandId: string | null;
  updatedByTenantAdmin: string | null;
}

export interface TenantAdminSummaryResponseV1 {
  schemaVersion: typeof tenantAdminSummaryContractVersion;
  tenantId: string;
  summary: {
    generatedAt: string;
    tenantDisplayName: string | null;
    memberships: {
      activeCount: number;
      adminCount: number;
      clinicianCount: number;
      siteCount: number;
    };
    connectors: {
      totalCount: number;
      statuses: TenantAdminSummaryConnectorStatusCountV1[];
      registrations: TenantAdminSummaryConnectorRegistrationV1[];
    };
    provisioning: TenantAdminSummaryProvisioningSnapshotV1;
    workflow: TenantAdminSummaryWorkflowSnapshotV1;
    audit: TenantAdminSummaryAuditSnapshotV1;
    activity: TenantAdminSummaryActivitySnapshotV1;
    analytics: TenantAdminSummaryAnalyticsSnapshotV1;
    workforceSubjects: TenantAdminSummaryWorkforceSubjectV1[];
    patientOperations: TenantAdminSummaryPatientOperationV1[];
    departments: TenantAdminSummaryDepartmentV1[];
    teams: TenantAdminSummaryTeamV1[];
    featureFlags: TenantAdminSummaryFeatureFlagV1[];
    aiModelParameters: TenantAdminAiModelParametersV1;
    callbackSchedulerSettings?: TenantAdminCallbackSchedulerSettingsV1 | null;
  };
}

export interface TenantAdminPatientOperationsRequestV1 {
  tenantId: string;
  searchQuery?: string | null;
  offset?: number | null;
  limit?: number | null;
  sortKey?: TenantAdminPatientOperationsSortKey | null;
  sortDirection?: TenantAdminPatientOperationsSortDirection | null;
  careTypeFilter?: TenantAdminPatientOperationsCareTypeFilter | null;
  includeArchived?: boolean | null;
  lifecycleFilter?: TenantAdminPatientOperationsLifecycleFilter | null;
}

export interface TenantAdminPatientOperationsResponseV1 {
  schemaVersion: typeof tenantAdminPatientOperationsContractVersion;
  tenantId: string;
  generatedAt: string;
  items: TenantAdminSummaryPatientOperationV1[];
  page: {
    offset: number;
    limit: number;
    totalCount: number;
    hasMore: boolean;
    searchQuery: string | null;
    sortKey: TenantAdminPatientOperationsSortKey;
    sortDirection: TenantAdminPatientOperationsSortDirection;
    careTypeFilter: TenantAdminPatientOperationsCareTypeFilter;
    includeArchived: boolean;
    lifecycleFilter: TenantAdminPatientOperationsLifecycleFilter;
  };
}

export interface TenantAdminClinicianOperationsRequestV1 {
  tenantId: string;
  searchQuery?: string | null;
  offset?: number | null;
  limit?: number | null;
  sortKey?: TenantAdminClinicianOperationsSortKey | null;
  sortDirection?: TenantAdminClinicianOperationsSortDirection | null;
  includeInactive?: boolean | null;
}

export interface TenantAdminClinicianOperationsResponseV1 {
  schemaVersion: typeof tenantAdminClinicianOperationsContractVersion;
  tenantId: string;
  generatedAt: string;
  items: TenantAdminClinicianOperationV1[];
  page: {
    offset: number;
    limit: number;
    totalCount: number;
    hasMore: boolean;
    searchQuery: string | null;
    sortKey: TenantAdminClinicianOperationsSortKey;
    sortDirection: TenantAdminClinicianOperationsSortDirection;
    includeInactive: boolean;
  };
}

export interface TenantAdminReviewQueueItemsRequestV1 {
  tenantId: string;
  queueId: TenantAdminReviewQueueId;
  offset?: number | null;
  limit?: number | null;
}

export interface TenantAdminReviewQueueItemV1 {
  itemId: string;
  itemKind: TenantAdminReviewQueueItemKind;
  queueId: TenantAdminReviewQueueId;
  title: string;
  previewText: string | null;
  sourceArea: string;
  patientId: string | null;
  patientIdentifier: string | null;
  patientDisplayName: string | null;
  ownerLabel: string | null;
  statusLabel: string;
  priorityLabel: string;
  occurredAt: string | null;
  updatedAt: string | null;
  routeLabel: string | null;
  metadataSummary: string | null;
}

export interface TenantAdminReviewQueueItemsResponseV1 {
  schemaVersion: typeof tenantAdminReviewQueueItemsContractVersion;
  tenantId: string;
  queueId: TenantAdminReviewQueueId;
  generatedAt: string;
  items: TenantAdminReviewQueueItemV1[];
  page: {
    offset: number;
    limit: number;
    totalCount: number;
    hasMore: boolean;
  };
}

export type TenantAdminCommandInputV1 =
  | {
      kind: "create-patient" | "update-patient";
      patientId: string;
      displayName: string;
      expectedLifecycleVersion?: number | null;
      givenName?: string | null;
      familyName?: string | null;
      identifier?: string | null;
      dateOfBirth?: string | null;
      sex?: "female" | "male" | null;
      emailAddress?: string | null;
      phoneNumber?: string | null;
      secondaryPhoneNumber?: string | null;
      workPhoneNumber?: string | null;
      homePhoneNumber?: string | null;
      emergencyContactPhone?: string | null;
      alternateEmergencyContactPhone?: string | null;
      addressStreet?: string | null;
      addressCity?: string | null;
      addressState?: string | null;
      addressZip?: string | null;
      leadingClinicianUserId?: string | null;
      leadingClinicianLabel?: string | null;
      programLabel?: string | null;
      contactWindowText?: string | null;
      siteId?: string | null;
      status?: TenantAdminPatientCommandStatus | null;
      onboardingInitialFlowComplete?: boolean | null;
    }
  | {
      kind: "import-patients-csv";
      csvText: string;
      fileName?: string | null;
      siteId?: string | null;
      status?: TenantAdminPatientCommandStatus | null;
      onboardingInitialFlowComplete?: boolean | null;
      mode?: "create-only" | null;
    }
  | {
      kind: "archive-patient";
      patientId: string;
      expectedLifecycleVersion: number;
      idempotencyKey: string;
      reasonKey: string;
      notes?: string | null;
    }
  | {
      kind: "restore-patient";
      patientId: string;
      expectedLifecycleVersion: number;
      idempotencyKey: string;
      reasonKey: string;
      notes?: string | null;
      restoreStatus?: TenantAdminPatientCommandStatus | null;
    }
  | {
      kind: "reconcile-patient-access";
      patientId: string;
      expectedLifecycleVersion: number;
    }
  | {
      kind: "create-clinician" | "update-clinician";
      clinicianUserId: string;
      displayName: string;
      emailAddress?: string | null;
      phoneNumber?: string | null;
      previewProfileKey?: string | null;
      testLoginEmail?: string | null;
      testLoginPassword?: string | null;
      siteCode?: string | null;
      departmentCode?: string | null;
      teamKey?: string | null;
      roleKey?: TenantRoleKey | null;
      status?: "active" | "inactive" | "suspended" | null;
    }
  | {
      kind: "retire-clinician";
      clinicianUserId: string;
    }
  | {
      kind: "create-department" | "update-department";
      departmentCode: string;
      displayName: string;
      siteCode?: string | null;
      status?: "draft" | "active" | "inactive" | "retired" | null;
    }
  | {
      kind: "retire-department";
      departmentCode: string;
    }
  | {
      kind: "create-team" | "update-team";
      teamKey: string;
      displayName: string;
      departmentCode?: string | null;
      status?: "draft" | "active" | "inactive" | "retired" | null;
    }
  | {
      kind: "retire-team";
      teamKey: string;
    }
  | {
      kind: "create-connector";
      displayName: string;
      connectorFamily: "mssql_direct" | "fhir_r4" | "epic" | "custom";
      sourceSystemKey: string;
      status?: "draft" | "active" | "paused" | "retired" | null;
      siteCode?: string | null;
      departmentCode?: string | null;
      teamKey?: string | null;
      researchPackSlug?: string | null;
      sourceAuthorityMode?: "incremental" | "snapshot_authoritative" | "mixed" | null;
      metadataSummary?: string | null;
    }
  | {
      kind: "update-connector";
      connectorId: string;
      displayName: string;
      status?: "draft" | "active" | "paused" | "retired" | null;
      siteCode?: string | null;
      departmentCode?: string | null;
      teamKey?: string | null;
      researchPackSlug?: string | null;
      sourceAuthorityMode?: "incremental" | "snapshot_authoritative" | "mixed" | null;
      metadataSummary?: string | null;
    }
  | {
      kind: "retire-connector";
      connectorId: string;
    }
  | {
      kind: "connector-sync-now";
      connectorId: string;
    }
  | {
      kind: "connector-auth-review";
      connectorId: string;
      authStatus: "reviewed" | "needs_attention" | "expired" | "blocked";
      status?: "draft" | "active" | "paused" | "retired" | null;
      notes?: string | null;
    }
  | {
      kind: "review-queue-action";
      queueId: TenantAdminReviewQueueId;
      actionKind: TenantAdminReviewQueueActionKind;
      ownerKind?: TenantAdminReviewQueueOwnerKind | null;
      ownerReference?: string | null;
      notes?: string | null;
      limit?: number | null;
    }
  | {
      kind: "patient-portal-control";
      patientId: string;
      actionKind: TenantAdminPatientPortalControlActionKind;
      entryPointRoute?: TenantAdminPatientPortalRoute | null;
      remindersEnabled?: boolean | null;
      wakeUpTime?: string | null;
      notes?: string | null;
    }
  | {
      kind: "set-patient-portal-feature-flags";
      featureFlags: PatientPortalFeatureFlagsV1;
      rolloutNote?: string | null;
    }
  | {
      kind: "set-feature-flag";
      flagKey: string;
      enabled: boolean;
      flagVariant?: string | null;
      scope?: "tenant" | "site" | "department" | "program" | null;
      scopeKey?: string | null;
      rolloutNote?: string | null;
    }
  | {
      kind: "set-system-flag";
      flagKey: string;
      enabled: boolean;
      rolloutNote?: string | null;
    }
  | {
      kind: "unset-feature-flag";
      flagKey: string;
      flagVariant?: string | null;
      scope?: "tenant" | "site" | "department" | "program" | null;
      scopeKey?: string | null;
      rolloutNote?: string | null;
    }
  | {
      kind: "unset-system-flag";
      flagKey: string;
      rolloutNote?: string | null;
    }
  | {
      kind: "update-callback-scheduler-settings";
      callbacksEnabled: boolean;
      retryBreakMinutes: number;
      maxAttemptsPerRequest: number;
    }
  | {
      kind: "set-ai-model-context-flag";
      modelKey: TenantAdminAiModelKeyV1;
      flagKey: TenantAdminAiModelContextFlagKeyV1;
      enabled: boolean;
    }
  | {
      kind: "upsert-ai-model-prompt-artifact";
      promptId?: string | null;
      modelKey: TenantAdminAiModelArtifactModelKeyV1;
      labelText: string;
      purposeText?: string | null;
      promptText: string;
      status?: TenantAdminAiModelArtifactStatusV1 | null;
    }
  | {
      kind: "archive-ai-model-prompt-artifact";
      promptId: string;
    }
  | {
      kind: "upsert-ai-model-source-document";
      sourceDocumentId?: string | null;
      modelKey: TenantAdminAiModelArtifactModelKeyV1;
      titleText: string;
      summaryText?: string | null;
      sourceText: string;
      status?: TenantAdminAiModelArtifactStatusV1 | null;
    }
  | {
      kind: "archive-ai-model-source-document";
      sourceDocumentId: string;
    }
  | {
      kind: "refresh-patient-summary";
      patientId: string;
      force?: boolean | null;
    }
  | {
      kind: "refresh-stale-patient-summaries";
      limit?: number | null;
      force?: boolean | null;
    };

export interface TenantAdminCommandRequestV1 {
  schemaVersion: typeof tenantAdminCommandContractVersion;
  tenantId: string;
  command: TenantAdminCommandInputV1;
}

export interface TenantAdminPatientImportIssueV1 {
  rowNumber: number;
  field: string | null;
  severity: "error" | "warning";
  message: string;
}

export interface TenantAdminPatientImportResultV1 {
  kind: "patient-import";
  fileName: string | null;
  accepted: boolean;
  createdCount: number;
  rejectedCount: number;
  warningCount: number;
  issues: TenantAdminPatientImportIssueV1[];
  patientIds: string[];
}

export type TenantAdminCommandResultV1 = TenantAdminPatientImportResultV1;

export interface TenantAdminCommandResponseV1 {
  schemaVersion: typeof tenantAdminCommandContractVersion;
  tenantId: string;
  commandId: string;
  appliedAt: string;
  summaryText: string;
  summary: TenantAdminSummaryResponseV1;
  result?: TenantAdminCommandResultV1 | null;
  changedScopes?: WorkspaceChangedScopeV1[];
}
